import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  marketplaceMVPBibleApi,
  type AdoptionDriver,
  type BacklogItem,
  type BusinessValueEntry,
  type CompetitivePosition,
  type CustomerSegment,
  type FeatureClassification,
  type FeatureValidation,
  type GoLiveChecklistItem,
  type MVPCriterion,
  type MVPBibleStatus,
  type MVPDashboard,
  type OutOfScopeItem,
  type PhilosophyOutcome,
  type ProductFamily,
  type ReleasePlan,
  type SuccessMetric,
} from '../../api/marketplace-mvp-bible';

type Tab = 'overview' | 'roadmap' | 'backlog' | 'value' | 'segments' | 'commercial' | 'golive' | 'status';
type Theme = 'light' | 'dark';

const classColors: Record<string, string> = {
  must_have: 'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200',
  should_have: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  could_have: 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200',
  future: 'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200',
  rejected: 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  never_build: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

export function MarketplaceMVPBiblePage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<MVPDashboard | null>(null);
  const [philosophy, setPhilosophy] = useState<PhilosophyOutcome[]>([]);
  const [families, setFamilies] = useState<ProductFamily[]>([]);
  const [classifications, setClassifications] = useState<FeatureClassification[]>([]);
  const [criteria, setCriteria] = useState<MVPCriterion[]>([]);
  const [releases, setReleases] = useState<ReleasePlan[]>([]);
  const [segments, setSegments] = useState<CustomerSegment[]>([]);
  const [valueMatrix, setValueMatrix] = useState<BusinessValueEntry[]>([]);
  const [competitive, setCompetitive] = useState<CompetitivePosition[]>([]);
  const [adoption, setAdoption] = useState<AdoptionDriver[]>([]);
  const [successMetrics, setSuccessMetrics] = useState<SuccessMetric[]>([]);
  const [outOfScope, setOutOfScope] = useState<OutOfScopeItem[]>([]);
  const [backlog, setBacklog] = useState<BacklogItem[]>([]);
  const [goLive, setGoLive] = useState<GoLiveChecklistItem[]>([]);
  const [status, setStatus] = useState<MVPBibleStatus | null>(null);
  const [featResult, setFeatResult] = useState<FeatureValidation | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, phil, fam, cls, crit, rel, seg, val, comp, adopt, metrics, oos, bl, gl, st] = await Promise.all([
        marketplaceMVPBibleApi.dashboard(),
        marketplaceMVPBibleApi.philosophy(),
        marketplaceMVPBibleApi.productFamilies(),
        marketplaceMVPBibleApi.featureClassifications(),
        marketplaceMVPBibleApi.mvpCriteria(),
        marketplaceMVPBibleApi.releases(),
        marketplaceMVPBibleApi.customerSegments(),
        marketplaceMVPBibleApi.businessValueMatrix(),
        marketplaceMVPBibleApi.competitivePositioning(),
        marketplaceMVPBibleApi.adoptionStrategy(),
        marketplaceMVPBibleApi.successMetrics(),
        marketplaceMVPBibleApi.outOfScope(),
        marketplaceMVPBibleApi.backlog(),
        marketplaceMVPBibleApi.goLiveChecklist(),
        marketplaceMVPBibleApi.status(),
      ]);
      setDashboard(dash);
      setPhilosophy(phil);
      setFamilies(fam);
      setClassifications(cls);
      setCriteria(crit);
      setReleases(rel);
      setSegments(seg);
      setValueMatrix(val);
      setCompetitive(comp);
      setAdoption(adopt);
      setSuccessMetrics(metrics);
      setOutOfScope(oos);
      setBacklog(bl);
      setGoLive(gl);
      setStatus(st);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load MVP bible');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const validateFeature = async () => {
    setBusy(true);
    setActionMsg(null);
    try {
      const result = await marketplaceMVPBibleApi.validateFeature({
        feature_key: 'opportunity_inbox',
        attrs: { business_justification: true, measurable_kpis: true, defined_release: true, commercial_value: true, success_criteria: true },
      });
      setFeatResult(result);
      setActionMsg(result.valid ? 'Feature passes MIB-000007 gate — approved for development' : `Gaps: ${result.gaps?.join(', ')}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Validation failed');
    } finally {
      setBusy(false);
    }
  };

  const card = dark ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const shell = dark ? 'bg-slate-950 text-slate-100' : 'bg-white text-slate-900';
  const accent = 'text-rose-500';
  const tabActive = 'bg-rose-600 text-white';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Executive Summary' },
    { id: 'roadmap', label: 'Release Roadmap' },
    { id: 'backlog', label: 'Prioritized Backlog' },
    { id: 'value', label: 'Business Value' },
    { id: 'segments', label: 'Customer Segments' },
    { id: 'commercial', label: 'Commercial & Adoption' },
    { id: 'golive', label: 'Go-Live & Scope' },
    { id: 'status', label: 'Bible Status' },
  ];

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>MIB-000007</p>
          <h1 className="text-2xl font-semibold">Marketplace Intelligence MVP & Release Roadmap</h1>
          <p className={`text-sm ${muted}`}>
            Feature prioritization, customer value matrix, release planning, commercial strategy and execution roadmap
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/platform/marketplace-intelligence-bible" className={`${accent} hover:underline`}>MIB-000001</Link>
            <span className={muted}>→</span>
            <Link to="/platform/marketplace-msi-bible" className={`${accent} hover:underline`}>MIB-000006</Link>
          </div>
        </div>
        <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>
          {dark ? 'Light mode' : 'Dark mode'}
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2 dark:border-slate-800">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? tabActive : `${card} ${muted}`}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Product Families', value: dashboard.product_families },
              { label: 'Release Plans', value: dashboard.release_plans },
              { label: 'Backlog Items', value: dashboard.backlog_items },
              { label: 'Business Value Entries', value: dashboard.business_value_entries },
              { label: 'Customer Segments', value: dashboard.customer_segments },
              { label: 'MVP Criteria', value: dashboard.mvp_criteria },
              { label: 'Go-Live Checklist', value: dashboard.go_live_checklist },
              { label: 'Out of Scope', value: dashboard.out_of_scope_items },
            ].map((item) => (
              <div key={item.label} className={`rounded-xl border p-4 ${card}`}>
                <p className={`text-xs uppercase ${muted}`}>{item.label}</p>
                <p className="mt-1 text-2xl font-semibold">{item.value}</p>
              </div>
            ))}
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Highest ROI Software — Not a Feature Factory</h3>
            <div className="mt-3 flex flex-wrap gap-2">
              {philosophy.map((p) => (
                <span key={p.key} className="rounded-full bg-rose-100 px-3 py-1 text-sm dark:bg-rose-900">{p.name}</span>
              ))}
            </div>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Product Families</h3>
            <div className="mt-3 grid gap-2 md:grid-cols-2 lg:grid-cols-3">
              {families.map((f) => (
                <div key={f.key} className="rounded-lg border p-3 dark:border-slate-700">
                  <span className="font-medium">{f.name}</span>
                  {f.description && <p className={`mt-1 text-sm ${muted}`}>{f.description}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'roadmap' && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Release Roadmap</h3>
            <div className="mt-3 space-y-4">
              {releases.map((r) => (
                <div key={r.key} className="rounded-lg border p-4 dark:border-slate-700">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <span className="font-mono text-sm text-rose-500">{r.version}</span>
                      <span className="ml-2 font-medium">{r.name}</span>
                    </div>
                    {r.target_quarter && <span className={`rounded px-2 py-0.5 text-xs ${muted}`}>{r.target_quarter}</span>}
                  </div>
                  <p className={`mt-2 text-sm font-medium ${muted}`}>Objectives</p>
                  <ul className="list-disc pl-5 text-sm">{r.objectives.map((o) => <li key={o}>{o}</li>)}</ul>
                  <p className={`mt-2 text-sm font-medium ${muted}`}>Features</p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {r.features.map((f) => <span key={f} className="rounded bg-rose-50 px-2 py-0.5 text-xs dark:bg-rose-950">{f}</span>)}
                  </div>
                  <p className={`mt-2 text-sm font-medium ${muted}`}>Success Criteria</p>
                  <ul className="list-disc pl-5 text-sm">{r.success_criteria.map((s) => <li key={s}>{s}</li>)}</ul>
                </div>
              ))}
            </div>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">MVP Selection Criteria (11 weighted factors)</h3>
            <div className="mt-3 grid gap-2 md:grid-cols-2">
              {criteria.map((c) => (
                <div key={c.key} className="flex items-center justify-between rounded border p-2 dark:border-slate-700">
                  <span>{c.name}</span>
                  <span className="font-mono text-sm text-rose-500">{(c.weight * 100).toFixed(0)}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'backlog' && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Prioritized Product Backlog</h3>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={`border-b ${muted}`}>
                    <th className="p-2 text-left">#</th>
                    <th className="p-2 text-left">Feature</th>
                    <th className="p-2 text-left">Family</th>
                    <th className="p-2 text-left">Class</th>
                    <th className="p-2 text-left">Release</th>
                    <th className="p-2 text-right">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {backlog.map((b) => (
                    <tr key={b.key} className="border-b dark:border-slate-700">
                      <td className="p-2 font-mono">{b.priority}</td>
                      <td className="p-2 font-medium">{b.feature}</td>
                      <td className="p-2 font-mono text-xs">{b.family}</td>
                      <td className="p-2"><span className={`rounded px-2 py-0.5 text-xs ${classColors[b.classification] ?? ''}`}>{b.classification.replace(/_/g, ' ')}</span></td>
                      <td className="p-2">{b.release}</td>
                      <td className="p-2 text-right font-mono">{b.score?.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Feature Classifications</h3>
            <div className="mt-3 grid gap-2 md:grid-cols-2 lg:grid-cols-3">
              {classifications.map((c) => (
                <div key={c.key} className="rounded-lg border p-3 dark:border-slate-700">
                  <span className={`rounded px-2 py-0.5 text-xs font-medium ${classColors[c.key] ?? ''}`}>{c.name}</span>
                  <p className={`mt-2 text-sm ${muted}`}>{c.description}</p>
                </div>
              ))}
            </div>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Validate Feature for Development</h3>
            <p className={`mt-1 text-sm ${muted}`}>No feature enters development without business justification, KPIs, release, commercial value and success criteria.</p>
            <button type="button" disabled={busy} onClick={validateFeature} className="mt-3 rounded-lg bg-rose-600 px-4 py-2 text-sm text-white disabled:opacity-50">
              {busy ? 'Validating…' : 'Validate Opportunity Inbox Feature'}
            </button>
            {featResult && (
              <div className="mt-4 grid gap-2 md:grid-cols-3 lg:grid-cols-5">
                {[
                  { label: 'Valid', value: featResult.valid ? 'Yes' : 'No' },
                  { label: 'Business Justification', value: featResult.has_business_justification ? 'Yes' : 'No' },
                  { label: 'Measurable KPIs', value: featResult.has_measurable_kpis ? 'Yes' : 'No' },
                  { label: 'Defined Release', value: featResult.has_defined_release ? 'Yes' : 'No' },
                  { label: 'Commercial Value', value: featResult.has_commercial_value ? 'Yes' : 'No' },
                ].map((item) => (
                  <div key={item.label} className="rounded-lg border p-3 dark:border-slate-700">
                    <p className={`text-xs ${muted}`}>{item.label}</p>
                    <p className="font-semibold">{item.value}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'value' && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <h3 className="font-medium">Business Value Matrix</h3>
          <div className="mt-3 space-y-3">
            {valueMatrix.map((v) => (
              <div key={v.key} className="rounded-lg border p-4 dark:border-slate-700">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium">{v.feature}</span>
                  {v.classification && <span className={`rounded px-2 py-0.5 text-xs ${classColors[v.classification] ?? ''}`}>{v.classification.replace(/_/g, ' ')}</span>}
                </div>
                <div className="mt-2 grid gap-2 text-sm md:grid-cols-2 lg:grid-cols-3">
                  <div><span className={muted}>Problem:</span> {v.problem_solved}</div>
                  <div><span className={muted}>Persona:</span> {v.target_persona}</div>
                  <div><span className={muted}>KPI:</span> {v.primary_kpi}</div>
                  <div><span className={muted}>Impact:</span> {v.expected_business_impact}</div>
                  <div><span className={muted}>Time to Value:</span> {v.expected_time_to_value}</div>
                  <div><span className={muted}>Commercial:</span> {v.commercial_value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'segments' && (
        <div className="grid gap-4 md:grid-cols-2">
          {segments.map((s) => (
            <div key={s.key} className={`rounded-xl border p-4 ${card}`}>
              <h4 className="font-medium">{s.segment}</h4>
              {s.description && <p className={`text-sm ${muted}`}>{s.description}</p>}
              {s.entry_release && <p className="mt-1 text-sm text-rose-500">Entry: {s.entry_release}</p>}
              <p className="mt-2 text-sm font-medium">Minimum Features</p>
              <div className="mt-1 flex flex-wrap gap-1">
                {s.minimum_features.map((f) => <span key={f} className="rounded bg-rose-50 px-2 py-0.5 text-xs dark:bg-rose-950">{f}</span>)}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'commercial' && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Competitive Positioning</h3>
            <div className="mt-3 space-y-3">
              {competitive.map((c) => (
                <div key={c.key} className="rounded-lg border p-3 dark:border-slate-700">
                  <span className="font-medium">{c.capability}</span>
                  <p className={`mt-1 text-sm ${muted}`}>Gap: {c.market_gap}</p>
                  <p className="text-sm text-rose-500">Differentiator: {c.differentiator}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-4">
            <div className={`rounded-xl border p-4 ${card}`}>
              <h3 className="font-medium">Adoption Strategy</h3>
              <div className="mt-3 space-y-2">
                {adoption.map((a) => (
                  <div key={a.key} className="rounded border p-2 dark:border-slate-700">
                    <span className="font-medium">{a.name}</span>
                    <span className={`ml-2 text-xs ${muted}`}>{a.phase}</span>
                    <p className={`text-sm ${muted}`}>{a.description}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className={`rounded-xl border p-4 ${card}`}>
              <h3 className="font-medium">Success Metrics</h3>
              <div className="mt-3 space-y-2">
                {successMetrics.map((m) => (
                  <div key={m.key} className="flex items-center justify-between rounded border p-2 dark:border-slate-700">
                    <span>{m.name}</span>
                    <span className="text-sm text-rose-500">{m.target}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'golive' && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Go-Live Checklist (V1.0)</h3>
            <div className="mt-3 space-y-2">
              {goLive.map((g) => (
                <div key={g.key} className="flex items-center justify-between rounded border p-3 dark:border-slate-700">
                  <span>{g.item}</span>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs ${muted}`}>{g.category}</span>
                    {g.required && <span className="text-xs text-rose-500">required</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Out of Scope</h3>
            <div className="mt-3 space-y-2">
              {outOfScope.map((o) => (
                <div key={o.key} className="rounded-lg border p-3 dark:border-slate-700">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{o.name}</span>
                    <span className={`rounded px-2 py-0.5 text-xs ${muted}`}>{o.category}</span>
                  </div>
                  <p className={`mt-1 text-sm ${muted}`}>{o.reason}</p>
                  {o.review_trigger && <p className="mt-1 text-xs text-amber-500">Review: {o.review_trigger}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'status' && status && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <h3 className="font-medium">MIB-000007 Bible Status</h3>
          <dl className="mt-4 grid gap-3 md:grid-cols-2">
            {Object.entries(status).map(([k, v]) => (
              <div key={k}>
                <dt className={`text-xs uppercase ${muted}`}>{k.replace(/_/g, ' ')}</dt>
                <dd className="font-medium">{Array.isArray(v) ? v.join(', ') : String(v)}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}
